/**
 * SuggestionsPeekSheet - Collapsible chip suggestions above the floating input.
 *
 * Two states:
 * - Collapsed: [Chips] ... [View more | RightSlot]
 * - Expanded: Scrollable list view with icon + label + description per item
 *
 * Animation: 0.3s ease-out-quint for smooth accordion feel
 *
 * Design: Part of unified card with FloatingAskHeidiInput - no border of its own,
 * separated by a subtle divider line.
 */

/* eslint-disable max-lines */

import {
  sortPeekSheetSuggestedChips,
  type AutoQueryStatus,
  type SuggestedChip,
} from '@/components/ui/transcript-chips/types';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Separator } from '@heidi/design-system/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { Info, Loader2, X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChipButton } from './chip-button';
import { type PinnedChip } from './evidence-settings-popover';
import { SuggestionListItem } from './suggestion-list-item';

// ============================================================================
// Constants
// ============================================================================

const EASE_OUT_QUINT = [0.23, 1, 0.32, 1] as const;
const COLLAPSED_HEIGHT = 44;
const TRANSITION_DURATION = 0.3;

/** Crossfade between the chip row and an arm's own collapsed row. */
const COLLAPSED_CROSSFADE_DURATION = 0.2;

/** Max chips shown in the collapsed row before "View more" appears */
const MAX_COLLAPSED_CHIPS = 3;

/** Max height for expanded list view before scrolling kicks in */
const MAX_EXPANDED_HEIGHT = 320;

// ============================================================================
// Types
// ============================================================================

interface SuggestionsPeekSheetProps {
  chips: SuggestedChip[];
  pinnedChips?: PinnedChip[];
  isTranscribing?: boolean;
  onChipClick?: (chip: SuggestedChip) => void;
  onPinnedChipClick?: (chip: PinnedChip) => void;
  queryStatuses?: Record<string, AutoQueryStatus>;
  loadingChipIds?: Set<string>;
  className?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Render slot for collapsed-row left-side action (e.g. history button) */
  collapsedLeftSlot?: React.ReactNode;
  /** Render slot for collapsed-row right-side action (e.g. view-mode dropdown) */
  collapsedRightSlot?: React.ReactNode;
  /**
   * Whether the collapsed row offers "View more" when the chips overflow.
   *
   * Off for the in-session composer: the capability-signalling experiment
   * measures whether the collapsed row alone gets a clinician to engage, and
   * a second affordance competing for that first click confounds the read.
   * Chips past `MAX_COLLAPSED_CHIPS` are then simply not offered there — the
   * row already sliced to that cap, and this drops the way to reach the rest.
   * The Evidence sidebar keeps it.
   */
  showViewMore?: boolean;
  /**
   * When true, `document_draft` chips are ordered before all other suggested chips
   * (document-suggestion experiment `chips` variant).
   */
  documentDraftSuggestionsFirst?: boolean;
  /**
   * Replacement for the collapsed row's chips — a verb starter row, or the
   * scoped-context row naming who the composer is working with.
   *
   * Rendered as a second absolutely-positioned layer over the chip row rather
   * than swapped for it, so `showCollapsedContent` can crossfade the two
   * without the sheet changing height. A hard swap reads as the sheet
   * flickering when the composer gains focus.
   */
  collapsedContent?: React.ReactNode;
  /** Which collapsed layer is on top. Ignored when `collapsedContent` is absent. */
  showCollapsedContent?: boolean;
  /**
   * Replacement for the expanded suggestion list — the level-2 starters of
   * whichever verb the clinician opened.
   *
   * The caller owns which verb that is, so the sheet stays the generic
   * collapsed/expanded pair it already was and the verb state does not
   * become a second state machine in here.
   */
  expandedContent?: React.ReactNode;
}

// ============================================================================
// Main Component
// ============================================================================

export const SuggestionsPeekSheet = memo(
  ({
    chips,
    pinnedChips = [],
    isTranscribing = false,
    onChipClick,
    onPinnedChipClick,
    queryStatuses = {},
    loadingChipIds = new Set(),
    className,
    expanded: controlledExpanded,
    onExpandedChange,
    collapsedLeftSlot,
    collapsedRightSlot,
    showViewMore = true,
    documentDraftSuggestionsFirst = false,
    collapsedContent,
    showCollapsedContent = false,
    expandedContent,
  }: SuggestionsPeekSheetProps) => {
    const intl = useIntl();
    const { t } = useTranslations();
    const closeLabel = intl.formatMessage({
      id: 'VA/Z1SW3vH',
      defaultMessage: 'Hide',
    });

    const [internalExpanded, setInternalExpanded] = useState(false);
    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;
    const containerRef = useRef<HTMLDivElement>(null);
    const expandedContentRef = useRef<HTMLDivElement>(null);
    const scrollListRef = useRef<HTMLDivElement>(null);
    const clinicalSectionBoundaryRef = useRef<HTMLDivElement>(null);
    const [expandedHeight, setExpandedHeight] = useState<number | null>(null);
    /** When document + clinical lists both show, header switches after scrolling past documents */
    const [scrollHeaderShowsClinical, setScrollHeaderShowsClinical] = useState(false);

    const sortedChips = useMemo(
      () => sortPeekSheetSuggestedChips(chips, documentDraftSuggestionsFirst),
      [chips, documentDraftSuggestionsFirst]
    );
    const documentDraftChips = useMemo(
      () => sortedChips.filter((c) => c.category === 'document_draft'),
      [sortedChips]
    );
    const transcriptPeekChips = useMemo(
      () => sortedChips.filter((c) => c.category !== 'document_draft'),
      [sortedChips]
    );
    /** Document-task chips are shown in their own labeled block when any are present */
    const hasDocumentSuggestionChips = documentDraftChips.length > 0;
    const hasTranscriptSuggestionChips = transcriptPeekChips.length > 0;
    const hasSuggestedChips = sortedChips.length > 0;
    const hasPinnedChips = pinnedChips.length > 0;
    const hasChips = hasSuggestedChips || hasPinnedChips;

    const allChipsForCollapsed = useMemo(
      () => (sortedChips.length > 0 ? sortedChips : pinnedChips),
      [sortedChips, pinnedChips]
    );

    const visibleChipsForCollapsed = allChipsForCollapsed.slice(0, MAX_COLLAPSED_CHIPS);
    const hasOverflow = allChipsForCollapsed.length > MAX_COLLAPSED_CHIPS;
    const showViewMoreButton = hasChips && hasOverflow && showViewMore;

    useLayoutEffect(() => {
      if (!expandedContentRef.current) return;
      const height = expandedContentRef.current.scrollHeight;
      setExpandedHeight(Math.min(height, MAX_EXPANDED_HEIGHT));
    }, [
      chips,
      hasSuggestedChips,
      hasDocumentSuggestionChips,
      hasTranscriptSuggestionChips,
      expandedContent,
    ]);

    const hasDualSuggestionSections =
      hasDocumentSuggestionChips && hasTranscriptSuggestionChips;

    const documentSuggestionsTitle = intl.formatMessage({
      id: 'nDs9FfBa4v',
      defaultMessage: 'Document Suggestions',
    });
    const clinicalConsiderationsTitle = intl.formatMessage({
      id: '6PKkdzJQao',
      defaultMessage: 'Clinical Considerations',
    });
    const showDocumentsTitle = hasDualSuggestionSections
      ? !scrollHeaderShowsClinical
      : hasDocumentSuggestionChips;

    const sheetHeaderTitle = showDocumentsTitle
      ? documentSuggestionsTitle
      : clinicalConsiderationsTitle;

    const updateScrollHeaderLabel = useCallback(() => {
      const scrollEl = scrollListRef.current;
      const boundary = clinicalSectionBoundaryRef.current;
      if (!scrollEl || !boundary || !hasDualSuggestionSections) {
        setScrollHeaderShowsClinical(false);
        return;
      }
      const scrollRect = scrollEl.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();
      setScrollHeaderShowsClinical(boundaryRect.top <= scrollRect.top + 1);
    }, [hasDualSuggestionSections]);

    useEffect(() => {
      if (!isExpanded || !hasDualSuggestionSections) {
        setScrollHeaderShowsClinical(false);
        return;
      }
      const scrollEl = scrollListRef.current;
      if (!scrollEl) return;

      updateScrollHeaderLabel();
      scrollEl.addEventListener('scroll', updateScrollHeaderLabel, { passive: true });
      const ro = new ResizeObserver(() => updateScrollHeaderLabel());
      ro.observe(scrollEl);

      return () => {
        scrollEl.removeEventListener('scroll', updateScrollHeaderLabel);
        ro.disconnect();
      };
    }, [
      isExpanded,
      hasDualSuggestionSections,
      updateScrollHeaderLabel,
      sortedChips.length,
    ]);

    useEffect(() => {
      setScrollHeaderShowsClinical(false);
    }, [chips]);

    const handleToggleExpand = useCallback(() => {
      const newValue = !isExpanded;
      if (isControlled) {
        onExpandedChange?.(newValue);
      } else {
        setInternalExpanded(newValue);
      }
    }, [isExpanded, isControlled, onExpandedChange]);

    const handleCollapse = useCallback(() => {
      if (isControlled) {
        onExpandedChange?.(false);
      } else {
        setInternalExpanded(false);
      }
    }, [isControlled, onExpandedChange]);

    // Allow chrome-only render (open-sidebar / history slot) when chips are
    // killed but shortcuts remain — otherwise focus shows an empty bar with no
    // way to open Evidence (EVI-2114 follow-up).
    // A capability-signalling arm can be the sheet's only reason to exist: its
    // verb row or context row is content even with no chips and no transcript
    // running, so it has to count here or the arm never renders.
    if (!hasChips && !isTranscribing && !collapsedRightSlot && !collapsedContent) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        data-testid="suggestions-peek-sheet"
        className={cn(
          'relative overflow-hidden',
          'rounded-b-none rounded-t-xl border border-b-0 border-border',
          // On the outer container, so it fills the peek row and the expanded
          // panel alike, for every capability-signalling arm including control.
          // Nothing inside paints an opaque plane over it — the list items
          // tint only their icon chip and their hover state.
          //
          // `surface-container` is the DS inset-container fill, and the ladder
          // it sits on inverts between themes: in newBrand it is #FEFCFA under
          // surface's #FFFFFF, so the sheet reads recessed against the
          // composer; in newBrandDark it is #1F1F1F OVER surface's #171717 and
          // above surface-elevated too, so it reads raised there.
          'bg-surface-container',
          'mx-3',
          className
        )}
      >
        <m.div
          initial={false}
          animate={{
            height: isExpanded ? (expandedHeight ?? 'auto') : COLLAPSED_HEIGHT,
          }}
          transition={{
            duration: TRANSITION_DURATION,
            ease: EASE_OUT_QUINT,
          }}
          className="relative overflow-hidden"
        >
          {/* ================================================================
              Expanded content - list view
              ================================================================ */}
          <m.div
            ref={expandedContentRef}
            initial={false}
            animate={{ opacity: isExpanded ? 1 : 0 }}
            transition={{
              duration: TRANSITION_DURATION * 0.6,
              ease: EASE_OUT_QUINT,
              delay: isExpanded ? TRANSITION_DURATION * 0.2 : 0,
            }}
            className={cn('flex flex-col', !isExpanded && 'pointer-events-none')}
          >
            {expandedContent ? (
              expandedContent
            ) : (
              <>
                {/* Header: title + settings + close */}
                <div className="flex shrink-0 items-center justify-between border-b border-border/30 px-4 py-2">
                  <span className="text-xs font-medium text-text-secondary">
                    {sheetHeaderTitle}
                  </span>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          onClick={handleCollapse}
                          className="size-7 text-text-tertiary hover:text-text-secondary"
                          aria-label={closeLabel}
                        >
                          <X size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">{closeLabel}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {/* Scrollable list content — suggestions only (shortcuts accessible via protocols menu) */}
                <div
                  ref={scrollListRef}
                  className="overflow-y-auto"
                  style={{ maxHeight: MAX_EXPANDED_HEIGHT - 40 }}
                >
                  {hasSuggestedChips && (
                    <div className="p-1">
                      {hasDocumentSuggestionChips ? (
                        <>
                          {documentDraftChips.map((chip) => (
                            <SuggestionListItem
                              key={chip.id}
                              chip={chip}
                              onClick={onChipClick}
                            />
                          ))}
                          {hasTranscriptSuggestionChips ? (
                            <>
                              <div
                                ref={clinicalSectionBoundaryRef}
                                className="h-px w-full shrink-0"
                                aria-hidden
                              />
                              <Separator className="my-2" />
                              <div className="px-2 pb-1 pt-0.5">
                                <TypographyCaption className="font-medium text-text-secondary">
                                  {clinicalConsiderationsTitle}
                                </TypographyCaption>
                              </div>
                              {transcriptPeekChips.map((chip) => (
                                <SuggestionListItem
                                  key={chip.id}
                                  chip={chip}
                                  onClick={onChipClick}
                                />
                              ))}
                            </>
                          ) : null}
                        </>
                      ) : (
                        sortedChips.map((chip) => (
                          <SuggestionListItem
                            key={chip.id}
                            chip={chip}
                            onClick={onChipClick}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </m.div>

          {/* ================================================================
              Collapsed content - chip row
              Layout: [Chips] ... [View more | RightSlot]

              Two absolutely-stacked layers when an arm supplies its own row,
              so the swap is a crossfade at a fixed height rather than the
              sheet resizing under the composer.
              ================================================================ */}
          <m.div
            initial={false}
            animate={{ opacity: isExpanded ? 0 : 1 }}
            transition={{
              duration: TRANSITION_DURATION * 0.5,
              ease: EASE_OUT_QUINT,
              delay: isExpanded ? 0 : TRANSITION_DURATION * 0.3,
            }}
            className={cn(
              'absolute inset-x-0 bottom-0 flex h-[44px] items-center pe-3',
              isExpanded && 'pointer-events-none'
            )}
          >
            {/* The crossfading pair, held to the space left of the shared
                right controls so neither layer runs under them. */}
            <div className="relative h-full min-w-0 flex-1">
              <m.div
                initial={false}
                animate={{ opacity: showCollapsedContent ? 0 : 1 }}
                transition={{
                  duration: COLLAPSED_CROSSFADE_DURATION,
                  ease: EASE_OUT_QUINT,
                }}
                className={cn(
                  'absolute inset-0 flex items-center ps-3',
                  showCollapsedContent && 'pointer-events-none'
                )}
                aria-hidden={showCollapsedContent}
              >
                {/* Left slot */}
                {collapsedLeftSlot && (
                  <div className="me-2 flex shrink-0 items-center">
                    {collapsedLeftSlot}
                  </div>
                )}

                {/* Chips */}
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden [mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)]">
                  {hasChips && (
                    <AnimatePresence mode="popLayout">
                      {visibleChipsForCollapsed.map((chip, idx) => (
                        <ChipButton
                          key={chip.id}
                          chip={chip}
                          index={idx}
                          suggestedChipsCount={sortedChips.length}
                          status={queryStatuses[chip.id]}
                          isNew={loadingChipIds.has(chip.id)}
                          onChipClick={chip.isPermanent ? onPinnedChipClick : onChipClick}
                          wrapperClassName={cn(
                            sortedChips.length > 1 && 'max-w-[180px]',
                            'overflow-hidden [&_span]:truncate'
                          )}
                        />
                      ))}
                    </AnimatePresence>
                  )}

                  {/* Empty state when transcribing but no chips yet */}
                  {!hasChips && isTranscribing && (
                    <div className="flex flex-1 items-center justify-center gap-2 text-sm text-text-tertiary">
                      <Loader2 size={14} className="animate-spin" />
                      <span>
                        <FormattedMessage
                          id="PAi3GY+s4M"
                          defaultMessage="Reviewing clinical considerations"
                        />
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info
                            size={14}
                            className="cursor-pointer text-text-tertiary transition-colors hover:text-text-secondary"
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          <FormattedMessage
                            id="p2ohXs1dNG"
                            defaultMessage="Evidence runs in the background to support clinical considerations"
                          />
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </m.div>

              {/* Arm-supplied row: verb starters, or the scoped-context row. Its
              own affordance drives expansion, so no "View more" here. */}
              {collapsedContent ? (
                <m.div
                  initial={false}
                  animate={{ opacity: showCollapsedContent ? 1 : 0 }}
                  transition={{
                    duration: COLLAPSED_CROSSFADE_DURATION,
                    ease: EASE_OUT_QUINT,
                  }}
                  className={cn(
                    'absolute inset-0 flex items-center ps-3',
                    !showCollapsedContent && 'pointer-events-none'
                  )}
                  aria-hidden={!showCollapsedContent}
                  data-testid="suggestions-peek-sheet-collapsed-content"
                >
                  <div className="flex min-w-0 flex-1 items-center overflow-hidden">
                    {collapsedContent}
                  </div>
                </m.div>
              ) : null}
            </div>

            {/* Right controls: View more + slot. Shared by both collapsed
                layers rather than rendered inside each, so the chat-history
                and sidebar buttons are one element and one tab stop on every
                arm — not a live copy plus an `aria-hidden` duplicate. */}
            {(showViewMoreButton || collapsedRightSlot) && (
              <div className="ms-2 flex shrink-0 items-center gap-1">
                {showViewMoreButton && (
                  <button
                    type="button"
                    onClick={handleToggleExpand}
                    className={cn(
                      'flex h-7 shrink-0 items-center justify-center rounded-md px-2',
                      'text-xs font-medium text-text-secondary',
                      'transition-colors hover:bg-surface-hover hover:text-text-primary'
                    )}
                    data-testid="view-more-button"
                  >
                    {t('chat.header.viewMore')}
                  </button>
                )}
                {collapsedRightSlot}
              </div>
            )}
          </m.div>
        </m.div>
      </div>
    );
  }
);

SuggestionsPeekSheet.displayName = 'SuggestionsPeekSheet';

export default SuggestionsPeekSheet;
